import { ChevronsLeft, MessageCircle, PanelLeftOpen, X } from "lucide-react";
import Image from "next/image";
import Link from "next/link";

import { Tooltip } from "@/components/ui/tooltip";
import { APP_BRAND } from "@/config/app-brand";
import { cn } from "@/lib/cn";

export interface ShellNavItem {
  href: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  visible: boolean;
}

export interface SidebarLabels {
  activeChannel: string;
  ready: string;
  notReady: string;
  navigationLabel: string;
  collapseSidebar: string;
  expandSidebar: string;
  closeNavigation: string;
}

interface SidebarPanelProps {
  panelId: string;
  navigationId: string;
  navItems: ShellNavItem[];
  pathname: string;
  providerReady: boolean | null;
  labels: SidebarLabels;
}

interface ExpandedSidebarPanelProps extends SidebarPanelProps {
  floating?: boolean;
  headerAction?: React.ReactNode;
  footerAction?: React.ReactNode;
  onNavigate?: () => void;
}

function isActiveNavItem(pathname: string, href: string) {
  return pathname === href || pathname.startsWith(`${href}/`);
}

function getProviderStatusLabel(providerReady: boolean | null, labels: SidebarLabels) {
  if (providerReady === true) {
    return labels.ready;
  }
  if (providerReady === false) {
    return labels.notReady;
  }
  return null;
}

function BrandLogo({ compact = false }: { compact?: boolean }) {
  if (compact) {
    return (
      <Image
        src={APP_BRAND.assets.sidebarMark}
        alt={APP_BRAND.productName}
        width={48}
        height={48}
        className="h-11 w-11 object-contain"
        priority
      />
    );
  }

  return (
    <Image
      src={APP_BRAND.assets.sidebarLogo}
      alt={APP_BRAND.productName}
      width={216}
      height={44}
      className="h-auto w-full max-w-54 object-contain object-left"
      priority
    />
  );
}

function ExpandedSidebarPanel({
  panelId,
  navigationId,
  navItems,
  pathname,
  providerReady,
  labels,
  floating = false,
  headerAction,
  footerAction,
  onNavigate,
}: ExpandedSidebarPanelProps) {
  return (
    <div
      id={panelId}
      className={cn(
        "flex h-full flex-col overflow-hidden bg-surface",
        floating ? "rounded-2xl border border-(--border) shadow-(--shadow)" : "border-r border-(--border)",
      )}
    >
      <div className="flex h-22.5 shrink-0 items-center gap-2 px-5">
        <div className="min-w-0 flex-1">
          <BrandLogo />
        </div>
        {headerAction}
      </div>

      <nav
        id={navigationId}
        aria-label={labels.navigationLabel}
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-3 py-4"
      >
        <div className="flex flex-col gap-2">
          {navItems.map((item) => {
            const active = isActiveNavItem(pathname, item.href);
            const Icon = item.icon;

            return (
              <Link
                key={item.href}
                href={item.href}
                aria-current={active ? "page" : undefined}
                onClick={onNavigate}
                className={cn(
                  "relative flex min-h-12 items-center gap-3 rounded-xl px-4 py-2 text-sm font-semibold transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none",
                  active
                    ? "bg-primary-soft text-primary before:absolute before:top-2 before:bottom-2 before:left-0 before:w-1 before:rounded-r-full before:bg-primary"
                    : "text-dark hover:bg-dark/5",
                )}
              >
                <Icon className={cn("h-5 w-5 shrink-0", active ? "text-primary" : "text-dark/55")} />
                <span className="truncate">{item.label}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      <div className="shrink-0 px-5 pb-5">
        <ChannelStatus providerReady={providerReady} labels={labels} />
      </div>

      {footerAction ? (
        <div
          className={cn(
            "flex h-22.5 shrink-0 items-center border-t border-(--border)",
            floating ? "px-3 xl:px-5" : "px-5",
          )}
        >
          {footerAction}
        </div>
      ) : null}
    </div>
  );
}

function ChannelStatus({ providerReady, labels }: Pick<SidebarPanelProps, "providerReady" | "labels">) {
  return (
    <div className="rounded-2xl border border-(--border) bg-primary-soft-muted px-4 py-4">
      <div className="flex items-center gap-2 text-xs font-medium text-(--muted)">
        <span
          aria-hidden="true"
          className={cn(
            "h-3 w-3 rounded-full",
            providerReady === true
              ? "bg-success-emphasis"
              : providerReady === false
                ? "bg-danger-emphasis"
                : "bg-muted",
          )}
        />
        <span>{labels.activeChannel}</span>
      </div>
      <div className="mt-2 flex items-center justify-between gap-3 text-sm font-semibold text-dark">
        <span>WhatsApp</span>
        {providerReady === true ? <span className="text-success">{labels.ready}</span> : null}
        {providerReady === false ? <span className="text-danger">{labels.notReady}</span> : null}
      </div>
    </div>
  );
}

function CompactSidebarPanel({ panelId, navigationId, navItems, pathname, providerReady, labels }: SidebarPanelProps) {
  const providerStatusLabel = getProviderStatusLabel(providerReady, labels);
  const channelLabel = `${labels.activeChannel}: WhatsApp${providerStatusLabel ? ` · ${providerStatusLabel}` : ""}`;

  return (
    <div id={panelId} className="flex h-full flex-col overflow-visible">
      <div className="flex h-22.5 shrink-0 items-center justify-center">
        <BrandLogo compact />
      </div>

      <nav
        id={navigationId}
        aria-label={labels.navigationLabel}
        className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-2.5 py-4"
      >
        <div className="flex flex-col gap-2">
          {navItems.map((item) => {
            const active = isActiveNavItem(pathname, item.href);
            const Icon = item.icon;

            return (
              <Tooltip key={item.href} content={item.label} side="right" className="w-full">
                <Link
                  href={item.href}
                  aria-label={item.label}
                  aria-current={active ? "page" : undefined}
                  className={cn(
                    "relative flex h-12 w-full items-center justify-center rounded-xl transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none",
                    active
                      ? "bg-primary-soft text-primary before:absolute before:top-2 before:bottom-2 before:left-0 before:w-1 before:rounded-r-full before:bg-primary"
                      : "text-dark/55 hover:bg-dark/5 hover:text-dark",
                  )}
                >
                  <Icon className="h-5 w-5" />
                </Link>
              </Tooltip>
            );
          })}
        </div>
      </nav>

      <div className="shrink-0 px-2.5 pb-5">
        <Tooltip content={channelLabel} side="right" className="w-full">
          <div
            role="status"
            aria-label={channelLabel}
            tabIndex={0}
            className="relative grid h-12 w-full place-items-center rounded-xl border border-(--border) bg-primary-soft-muted focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none"
          >
            <MessageCircle
              className={cn(
                "h-5 w-5",
                providerReady === true
                  ? "text-success-emphasis"
                  : providerReady === false
                    ? "text-danger-emphasis"
                    : "text-dark/40",
              )}
            />
            <span
              aria-hidden="true"
              className={cn(
                "absolute top-2 right-2 h-2 w-2 rounded-full ring-2 ring-surface",
                providerReady === true ? "bg-success-fill" : providerReady === false ? "bg-danger-fill" : "bg-muted",
              )}
            />
          </div>
        </Tooltip>
      </div>
    </div>
  );
}

export function DesktopSidebar({
  collapsed,
  onCollapsedChange,
  ...panelProps
}: SidebarPanelProps & {
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
}) {
  const toggleLabel = collapsed ? panelProps.labels.expandSidebar : panelProps.labels.collapseSidebar;

  return (
    <aside
      className={cn(
        "sticky top-0 hidden h-screen shrink-0 self-start p-3 motion-safe:transition-[width] motion-safe:duration-200 motion-safe:ease-out motion-reduce:transition-none lg:block",
        collapsed ? "w-[6.5rem]" : "w-[15.5rem] xl:w-[18.625rem]",
      )}
    >
      {collapsed ? (
        <div className="flex h-full flex-col rounded-2xl border border-(--border) bg-surface shadow-(--shadow)">
          <div className="min-h-0 flex-1">
            <CompactSidebarPanel {...panelProps} />
          </div>
          <div className="flex h-22.5 shrink-0 items-center justify-center border-t border-(--border)">
            <Tooltip content={toggleLabel} side="right">
              <button
                type="button"
                onClick={() => {
                  onCollapsedChange(false);
                }}
                aria-label={toggleLabel}
                aria-controls={panelProps.panelId}
                aria-expanded={false}
                className="grid h-11 w-11 place-items-center rounded-xl text-dark/65 transition-colors hover:bg-primary-soft hover:text-primary focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none"
              >
                <PanelLeftOpen className="h-5 w-5" />
              </button>
            </Tooltip>
          </div>
        </div>
      ) : (
        <ExpandedSidebarPanel
          {...panelProps}
          floating
          footerAction={
            <button
              type="button"
              onClick={() => {
                onCollapsedChange(true);
              }}
              aria-label={toggleLabel}
              aria-controls={panelProps.panelId}
              aria-expanded
              className="flex h-11 w-full items-center gap-2 rounded-xl px-3 font-medium text-dark/70 transition-colors hover:bg-primary-soft hover:text-primary focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none xl:gap-3"
            >
              <ChevronsLeft className="h-5 w-5" />
              <span className="text-xs whitespace-nowrap xl:text-sm">{toggleLabel}</span>
            </button>
          }
        />
      )}
    </aside>
  );
}

export function MobileSidebarDrawer({
  open,
  onClose,
  drawerRef,
  closeButtonRef,
  ...panelProps
}: ExpandedSidebarPanelProps & {
  open: boolean;
  onClose: () => void;
  drawerRef: React.RefObject<HTMLDivElement | null>;
  closeButtonRef: React.RefObject<HTMLButtonElement | null>;
}) {
  return (
    <>
      <button
        type="button"
        tabIndex={-1}
        aria-label={panelProps.labels.closeNavigation}
        disabled={!open}
        onClick={onClose}
        className={cn(
          "fixed inset-0 z-40 bg-overlay backdrop-blur-[1px] transition-opacity duration-200 motion-reduce:transition-none lg:hidden",
          open ? "opacity-100" : "pointer-events-none opacity-0",
        )}
      />

      <div
        ref={drawerRef}
        role="dialog"
        aria-modal={open ? "true" : undefined}
        aria-label={panelProps.labels.navigationLabel}
        aria-hidden={!open}
        inert={!open}
        data-modal-isolation-hidden={open ? "false" : "true"}
        className={cn(
          "fixed inset-y-0 left-0 z-50 w-[min(18rem,calc(100vw-3rem))] transform transition-transform duration-200 ease-out motion-reduce:transition-none lg:hidden",
          open ? "translate-x-0" : "pointer-events-none -translate-x-full",
        )}
      >
        <ExpandedSidebarPanel
          {...panelProps}
          onNavigate={onClose}
          headerAction={
            <button
              ref={closeButtonRef}
              type="button"
              onClick={onClose}
              aria-label={panelProps.labels.closeNavigation}
              className="grid h-10 w-10 shrink-0 place-items-center rounded-xl text-(--muted) transition-colors hover:bg-primary-soft hover:text-primary focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:outline-none"
            >
              <X className="h-5 w-5" />
            </button>
          }
        />
      </div>
    </>
  );
}
